import { isNil } from "lodash";
import {
  BenchStepOutcome,
  BenchStepState,
  EdiTransactionStatus,
  type BenchOrderReportFieldsFragment,
  type BenchStepFieldsFragment,
} from "../../generated/graphql";
import { stepLabel } from "./bench-steps";

/**
 * The selected order tab after a report update: the order the operator picked
 * while it is still in the report, else the first order, else none. A run
 * replaces the report every round, so the tab is keyed by order uuid.
 */
export const resolveSelectedOrder = (
  orderUuids: readonly string[],
  selected: string | null,
): string | null =>
  selected !== null && orderUuids.includes(selected)
    ? selected
    : (orderUuids[0] ?? null);

/** The longest HAWB a tab label shows beside the order name. */
const MAX_TAB_HAWB_LENGTH = 16;

export const orderTabLabel = (
  order: BenchOrderReportFieldsFragment,
): string => {
  const name = order.orderName ?? order.orderUuid;
  const { hawb } = order;
  return isNil(hawb) || hawb === name || hawb.length > MAX_TAB_HAWB_LENGTH
    ? name
    : `${name} · ${hawb}`;
};

export type OrderMarker =
  | "executed"
  | "waiting"
  | "complete"
  | "pending"
  | "blocked"
  | "refused"
  | "failed";

const MARKER_BY_OUTCOME: Record<BenchStepOutcome, OrderMarker> = {
  [BenchStepOutcome.Executed]: "executed",
  [BenchStepOutcome.Waiting]: "waiting",
  [BenchStepOutcome.LifecycleComplete]: "complete",
  [BenchStepOutcome.Blocked]: "blocked",
  [BenchStepOutcome.Refused]: "refused",
  [BenchStepOutcome.Failed]: "failed",
};

/**
 * The order's state for its tab: the outcome of its last step in a run, or,
 * in a dry run, what the plan says the next call would do.
 */
export const orderMarker = (
  order: BenchOrderReportFieldsFragment,
): OrderMarker => {
  const outcome = order.lastResult?.outcome;
  if (outcome !== undefined) {
    return MARKER_BY_OUTCOME[outcome];
  }
  if (order.refusals.length > 0) {
    return "refused";
  }
  if (isNil(order.nextStep)) {
    return "complete";
  }
  return order.nextStep.state === BenchStepState.Blocked
    ? "blocked"
    : "pending";
};

export const MARKER_COLOR: Record<
  OrderMarker,
  "success" | "info" | "warning" | "error" | "default"
> = {
  executed: "success",
  waiting: "info",
  complete: "default",
  pending: "warning",
  blocked: "error",
  refused: "error",
  failed: "error",
};

export type RunBlocker = {
  /** Unique within one report. */
  key: string;
  orderUuid: string;
  orderLabel: string;
  /** The step, or the EDI transaction, the reason is about. */
  subject: string;
  reason: string;
};

const BLOCKING_OUTCOMES: ReadonlySet<BenchStepOutcome> = new Set([
  BenchStepOutcome.Blocked,
  BenchStepOutcome.Refused,
  BenchStepOutcome.Failed,
]);

const NOT_SENT_STATUSES: ReadonlySet<EdiTransactionStatus> = new Set([
  EdiTransactionStatus.Failed,
  EdiTransactionStatus.Ignored,
]);

const stepSubject = (
  kind: string | null | undefined,
  stopRole: string | null | undefined,
  outcome: BenchStepOutcome,
): string => {
  const step = isNil(kind) ? "Order" : stepLabel(kind, stopRole);
  return `${step} ${outcome}`;
};

/**
 * Every reason in a run report that a step or an EDI message did not go out,
 * one entry per reason, for the error area above the order tabs: a blocked,
 * refused or failed last step, the guard's refusals, and each FAILED or
 * IGNORED outbound row with a message. A refused last step repeats the
 * refusals it was refused for, so those refusals are not listed twice.
 */
export const collectRunBlockers = (
  orders: readonly BenchOrderReportFieldsFragment[],
): RunBlocker[] =>
  orders.flatMap((order) => {
    const orderLabel = orderTabLabel(order);
    const blockers: RunBlocker[] = [];
    const last = order.lastResult;
    if (!isNil(last) && BLOCKING_OUTCOMES.has(last.outcome)) {
      blockers.push({
        key: `${order.orderUuid}:step`,
        orderUuid: order.orderUuid,
        orderLabel,
        subject: stepSubject(last.kind, last.stopRole, last.outcome),
        reason: last.message,
      });
    }
    const lastMessage = last?.message ?? "";
    for (const [index, refusal] of order.refusals.entries()) {
      if (!lastMessage.includes(refusal)) {
        blockers.push({
          key: `${order.orderUuid}:refusal:${index}`,
          orderUuid: order.orderUuid,
          orderLabel,
          subject: "Refused",
          reason: refusal,
        });
      }
    }
    for (const row of order.ediTransactions) {
      const { message } = row;
      if (
        NOT_SENT_STATUSES.has(row.status) &&
        !isNil(message) &&
        message.length > 0
      ) {
        blockers.push({
          key: `${order.orderUuid}:edi:${row.uuid}`,
          orderUuid: order.orderUuid,
          orderLabel,
          subject: `${row.transactionSet} ${
            row.edi214ShipmentStatus ?? row.edi990ActionCode ?? ""
          } ${row.status}`.replaceAll(/\s+/g, " "),
          reason: message,
        });
      }
    }
    return blockers;
  });

/**
 * A step's expected EDI, each entry led by its transaction set so a 214 reads
 * apart from a 990 or 210: "214 PICKUP_DISPATCHED", "990 ACCEPTED", "210".
 * The only step that expects a 990 is the tender acceptance, which sends
 * ACCEPTED.
 */
export const formatExpectedEdi = (
  step: Pick<
    BenchStepFieldsFragment,
    "expected214Statuses" | "expects990" | "expects210"
  >,
): string[] => [
  ...step.expected214Statuses.map((status) => `214 ${status}`),
  ...(step.expects990 ? ["990 ACCEPTED"] : []),
  ...(step.expects210 ? ["210"] : []),
];
